import { Component, OnInit, Output, EventEmitter, ViewChildren, ViewContainerRef, QueryList, ViewChild, OnDestroy, AfterViewInit, Input, OnChanges, SimpleChanges, Injector, ElementRef, NgZone } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatRow } from '@angular/material/table';
import { BaseComponent } from 'src/app/common/base/base.component';
import { StatusPendingCatalog, ActionPendingCatalog, PendingCatalogColor, Roles } from 'src/app/common/enumerations/enumerations';
import { AcceleratorCatalog } from 'src/app/components/catalogs/accelerator-catalog/services/accelerator-catalog';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { CatalogService } from '../../catalog-service';
import { PendingCatalogType } from 'src/app/services/pending-approvals/pending-approval';
import { Subscription, take } from 'rxjs';


@Component({
  selector: 'app-accelerator-catalog-list',
  templateUrl: './accelerator-catalog-list.component.html',
  styleUrls: ['./accelerator-catalog-list.component.scss']
})
export class AcceleratorCatalogListComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() filter!: string;

  @Output() rowSelected = new EventEmitter<any>();

  acceleratorCatalog?: AcceleratorCatalog;
  acceleratorCatalogs?: AcceleratorCatalog[];
  acceleratorCatalogsFiltered?: AcceleratorCatalog[];
  acceleratorCatalogsSubs!: Subscription;

  canCreate!: boolean;
  canApprove!: boolean;

  selectedRowIndex!: number;
  selectedIndex!: number;
  private selectionToRestore?: number;

  displayedColumnsAccelerator: string[] = ['name', 'status'];
  dataSource!: MatTableDataSource<AcceleratorCatalog>;

  sharedChangesRestrictionMessage = '';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('viewport', { static: true }) viewport!: ElementRef<HTMLElement>;
  @ViewChildren('matrow', { read: ElementRef }) rows!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    protected override injector: Injector,
    private catalogService: CatalogService, private zone: NgZone) {
    super(injector);
  }

  async ngOnInit() {
    this.loadAccelerators();
    this.sharedChangesRestrictionMessage =
      this.getMessage('PendingSharedChangesRestrictionMessage')?.description ?? '';
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  override ngOnDestroy(): void {
    this.acceleratorCatalogsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Loads catalog rows and restores the accelerator selected before a save refresh.
   *
   * @param id Accelerator ID to keep selected after refreshed store values arrive.
   */
  loadAccelerators(id?: number) {
    if (id !== undefined) {
      this.selectionToRestore = id;
    }
    // Keep one store subscription so an older refresh cannot clear the restored selection.
    this.acceleratorCatalogsSubs?.unsubscribe();
    const accelerator = this.catalogService.pendingCatalog?.approvalType == PendingCatalogType.Accelerator ? this.catalogService.pendingCatalog : null;
    this.canApprove = this.hasRoles([Roles.RP]);
    this.canCreate = this.hasPrivilege(PrivilegeEnum.AcceleratorCatalogCreate);
    this.acceleratorCatalogsSubs = this.store.select(state => state.AcceleratorCatalogs.data).subscribe((data: AcceleratorCatalog[]) => {
      data?.map(x => {
        if (x.pendingAcceleratorCatalog) {
          x.pendingAcceleratorCatalog.statusName = StatusPendingCatalog[x.pendingAcceleratorCatalog.status];
          x.pendingAcceleratorCatalog.actionName = ActionPendingCatalog[x.pendingAcceleratorCatalog.action];
        }
        if (x.lastPendingAccelerator)
          x.lastPendingAccelerator.statusByUser = this.users.find(u => u.id == x.lastPendingAccelerator?.statusBy);
      });
      this.dataSource = new MatTableDataSource(data);
      this.setSortingAccessor();
      this.dataSource.filter = this.filter;

      if (accelerator) {
        if (accelerator.id) {
          this.showIndex(accelerator.id);
        }
        else if (!accelerator.id) {
          this.showIndex(accelerator.pendingID);
        }
      } else if (this.selectionToRestore) {
        this.showIndex(this.selectionToRestore);
      }
      else if (this.selectionToRestore == 0) {
        const newID = data?.find(x => x.pendingAcceleratorCatalog?.action == ActionPendingCatalog.Create)?.id ?? 0;
        this.showIndex(newID);
      }
    }
    )
  }

  setSortingAccessor() {
    // Define the sorting accessor
    this.dataSource.sortingDataAccessor = (element, property) => {
      switch (property) {
        case 'name':
          return element.details.accelerator
            ? element.details.accelerator.name
            : element.pendingDetails?.accelerator?.name;
        case 'status':
          return element.pendingAcceleratorCatalog?.statusName +
            " - " +
            element.pendingAcceleratorCatalog?.actionName;
        default:
          return (element as any)[property]; // Fallback to the default property
      }
    };
    this.setSort<AcceleratorCatalog>(this.dataSource, this.sort);
  }

  async selectedRow(row: AcceleratorCatalog) {
    if (await this.canDeactivate())
      return this.selectRow(this.utils.cloneDeep(row));
  }

  selectRow(row: AcceleratorCatalog) {
    if (row && this.selectedRowIndex != row.id) {
      this.selectedRowIndex = row.id;
      this.rowSelected.emit(row);
    } else {
      this.selectedRowIndex = -1;
      this.rowSelected.emit(null);
    }
  }

  createResource() {
    this.selectedRowIndex = -1;
    this.rowSelected.emit(new AcceleratorCatalog());
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  getColor(resource: Resource): string {
    if (resource.color === PendingCatalogColor.Create || resource.color === PendingCatalogColor.Modify) {
      return '#EAAA00';
    }
    return '';
  }

  showIndex(id: number) {
    this.selectedIndex = this.dataSource.data.findIndex(x => x.id === id);
    this.selectedRowIndex = -1;
    this.showElement(this.selectedIndex);
    this.selectedRow(this.dataSource.data[this.selectedIndex]);
  }

  showElement(position: number) {
    // wait until Angular finished rendering (rows exist / updated)
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      const pos = String(position);

      const rowRef = this.rows.toArray().find(r =>
        r.nativeElement.getAttribute('position') === pos
      );

      if (!rowRef) {
        console.warn('Row not found for position:', position);
        return;
      }

      const rowEl = rowRef.nativeElement;
      const viewportEl = this.viewport.nativeElement;

      const rowRect = rowEl.getBoundingClientRect();
      const viewportRect = viewportEl.getBoundingClientRect();

      const isAbove = rowRect.top < viewportRect.top;
      const isBelow = rowRect.bottom > viewportRect.bottom;

      if (isAbove || isBelow) {
        // Bring it into view within its nearest scrollable container
        rowEl.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      }
    });
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex(r => {
      return r.id === row.id;
    });
    return i;
  }

  // public tooltipCache = new Map<number, string>();

  // buildTooltipCache(): void {
  //   const template =
  //     this.getMessage('PendingSharedChangesRestrictionMessage')?.description ?? '';

  //   const accelerators =
  //     this.dataSource.data
  //       ?.map(item => item.details?.accelerator)
  //       .filter((r): r is NonNullable<typeof r> => !!r) ?? [];

  //   const acceleratorMap = new Map(
  //     accelerators.map(r => [r.id, r.name])
  //   );

  //   this.dataSource.data?.forEach(ac => {
  //     const current = ac?.details?.accelerator;
  //     if (!current) return;

  //     const sharedIds = ac.sharedChangesParentsIDs ?? [];

  //     const others = sharedIds
  //       .map(id => acceleratorMap.get(id))
  //       .filter(Boolean)
  //       .join(', ');

  //     const message = template
  //       .replaceAll('{this}', current.name ?? '')
  //       .replaceAll('{other}', others);

  //     this.tooltipCache.set(current.id, message);
  //   });
  // }


}
